/**
 * Drill Plans API — client-side apiFetch wrapper.
 *
 * All Firestore access for `drill-plans` and `drillPlanAssignments` goes through
 * the server-side Express routes. Client code must not call Firestore directly.
 *
 * @see src/lib/routes/drill-plans-routes.ts
 * @see src/lib/routes/clients-routes.ts (assignment sub-routes)
 */

import { apiFetch } from '@/lib/api-client';
import type { DrillPlan, DrillPlanAssignment } from '../types/drill-plan-types';

// ---------------------------------------------------------------------------
// Drill-plan definitions (globally readable, creator/admin writeable)
// ---------------------------------------------------------------------------

interface CreateDrillPlanResponse {
  success: boolean;
  planId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

/**
 * Fetch all drill plans, optionally filtered by a name/tag query string.
 */
export async function listDrillPlans(q?: string): Promise<DrillPlan[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return apiFetch<DrillPlan[]>(`/drill-plans${qs}`);
}

/**
 * Fetch a single drill plan by ID.
 */
export async function getDrillPlan(planId: string): Promise<DrillPlan | null> {
  try {
    return await apiFetch<DrillPlan>(`/drill-plans/${planId}`);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes('not-found') || msg.includes('404')) return null;
    throw err;
  }
}

/**
 * Create a new drill plan. Returns the new plan ID.
 */
export async function createDrillPlan(
  data: Omit<DrillPlan, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const res = await apiFetch<CreateDrillPlanResponse>('/drill-plans', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.planId;
}

/**
 * Update an existing drill plan (partial update).
 */
export async function updateDrillPlan(
  planId: string,
  updates: Partial<Omit<DrillPlan, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  await apiFetch<UpdateResponse>(`/drill-plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a drill plan (creator or admin only).
 */
export async function deleteDrillPlan(planId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`/drill-plans/${planId}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Drill-plan assignments (practitioner-private, scoped to a client)
// ---------------------------------------------------------------------------

interface CreateAssignmentResponse {
  success: boolean;
  assignmentId: string;
  message: string;
}

/**
 * List drill-plan assignments for a client (only returns assignments the current
 * practitioner created).
 */
export async function listDrillPlanAssignments(
  clientId: string
): Promise<DrillPlanAssignment[]> {
  return apiFetch<DrillPlanAssignment[]>(
    `/clients/${clientId}/drill-plan-assignments`
  );
}

/**
 * Create a drill-plan assignment for a client. Returns the new assignment ID.
 */
export async function createDrillPlanAssignment(
  clientId: string,
  data: Omit<DrillPlanAssignment, 'id' | 'assignedAt' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const res = await apiFetch<CreateAssignmentResponse>(
    `/clients/${clientId}/drill-plan-assignments`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return res.assignmentId;
}

/**
 * Delete a drill-plan assignment.
 */
export async function deleteDrillPlanAssignment(
  clientId: string,
  assignmentId: string
): Promise<void> {
  await apiFetch<UpdateResponse>(
    `/clients/${clientId}/drill-plan-assignments/${assignmentId}`,
    { method: 'DELETE' }
  );
}

